import { useModal } from "@/features/modal/ModalStackManager";
import { Calendar, CalendarDate } from "@/features/ui/calendar";
import { PressWorkTimeDualChart } from "@/features/ui/PMS/chart/\bpressTimeBarChart";
import { IdleTimeChart } from "@/features/ui/PMS/chart/IdleTimeChart";
import { machineMoupData } from "@/features/ui/PMS/machineSearchBox";
import styled from "@emotion/styled";
import { Button, Flex, Text } from "@mantine/core";
import { IconCalendar } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useState } from "react";
import DataGrid, { Column } from "react-data-grid";

export const AnalysisIdleTimePage = () => {
  const [selectedMachines, setSelectedMachines] = useState<string[]>([]);
  const { openModal, closeModal } = useModal();
  const [dates, setDates] = useState<CalendarDate>([null, null]);
  const [machineID, setMachineID] = useState<string | null>(null);

  const handleSelectMachine = (machine: { id: string; name: string }) => {
    setSelectedMachines((prev) =>
      prev.includes(machine.id) ? prev.filter((m) => m !== machine.id) : [...prev, machine.id],
    );
    setMachineID(machine.id);
  };

  const pressColumns: readonly Column<any>[] = [
    {
      key: "pressName",
      name: "프레스 이름",
      resizable: true,
      sortable: true,
    },
    {
      key: "date",
      name: "날짜",
      resizable: true,
      sortable: true,
    },
    {
      key: "amount",
      name: "생산량",
      resizable: true,
      sortable: true,
    },
    {
      key: "uph",
      name: "UPH",
      resizable: true,
      sortable: true,
    },
    {
      key: "startTime",
      name: "시작 시간",
      resizable: true,
      sortable: true,
    },
    {
      key: "endTime",
      name: "종료 시간",
      resizable: true,
      sortable: true,
    },
    {
      key: "totalTime",
      name: "총 작동 시간",
      resizable: true,
      sortable: true,
    },
    {
      key: "workTime",
      name: "가동 시간",
      resizable: true,
      sortable: true,
    },
    {
      key: "idleTime",
      name: "비 가동 시간",
      resizable: true,
      sortable: true,
    },
    {
      key: "off",
      name: "전원 off",
      resizable: true,
      sortable: true,
    },
    {
      key: "waitTime",
      name: "대기 시간",
      resizable: true,
      sortable: true,
    },
    {
      key: "moldChangeTime",
      name: "금형교체 시간",
      resizable: true,
      sortable: true,
    },
    {
      key: "errorTime",
      name: "에러 시간",
      resizable: true,
      sortable: true,
    },
  ];

  return (
    <Flex gap={"md"} direction={"column"} p="md">
      <Text size="xl">비 가동 시간</Text>
      <ItemContainer
        style={{
          overflowX: "auto",
          scrollSnapType: "x mandatory",
          marginTop: 0,
          gap: "8px",
        }}
      >
        {machineMoupData.map((machine) => (
          <Button
            key={machine.id}
            onClick={() => handleSelectMachine(machine)}
            variant={selectedMachines.includes(machine.id) ? "filled" : "light"}
            color="blue"
          >
            {machine.name}
          </Button>
        ))}
      </ItemContainer>
      <ChartWrapper>
        <Flex justify={"flex-end"}>
          <Button
            leftIcon={<IconCalendar />}
            color="lime"
            onClick={() => {
              openModal(<Calendar dates={[null, null]} type="default" />, null, "날짜 선택").then(
                (selectedDates) => {
                  if (selectedDates) {
                    setDates(selectedDates);
                  }
                },
              );
            }}
          >
            {`${dates[0] !== null ? dayjs(dates[0]).format("YYYY-MM-DD") : ""} 날짜선택`}
          </Button>
        </Flex>
        <IdleTimeChart
          selectedMachines={selectedMachines}
          selectedMonth={`${dates[0] !== null ? dayjs(dates[0]).format("YYYY-MM") : ""}`}
        />
      </ChartWrapper>
      <DataGrid
        columns={pressColumns ?? []}
        rows={pressMockupData[Number(machineID) ?? 0] ?? []}
        rowHeight={40}
        style={{ height: "120px" }}
      />
      <PressWorkTimeDualChart machine={Number(machineID)} />
    </Flex>
  );
};

const ItemContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  margin-bottom: 16px;
`;

const ChartWrapper = styled.div`
  margin-top: 1rem;
`;

export const pressMockupData: Record<
  number,
  Array<{
    pressName: string;
    date: string;
    amount: number;
    uph: number;
    startTime: string;
    endTime: string;
    totalTime: string;
    workTime: string;
    idleTime: string;
    off: string;
    waitTime: string;
    moldChangeTime: string;
    errorTime: string;
  }>
> = {
  1: [
    {
      pressName: "Press 1",
      date: "2025-06-10",
      amount: 125,
      uph: 115,
      startTime: "08:37",
      endTime: "11:04",
      totalTime: "02:27",
      workTime: "01:52",
      idleTime: "00:35",
      off: "00:26",
      waitTime: "00:18",
      moldChangeTime: "00:03",
      errorTime: "00:20",
    },
  ],
  2: [
    {
      pressName: "Press 2",
      date: "2025-06-12",
      amount: 112,
      uph: 88,
      startTime: "08:33",
      endTime: "10:07",
      totalTime: "01:34",
      workTime: "01:00",
      idleTime: "00:34",
      off: "00:29",
      waitTime: "00:06",
      moldChangeTime: "00:25",
      errorTime: "00:02",
    },
  ],
  3: [
    {
      pressName: "Press 3",
      date: "2025-06-15",
      amount: 101,
      uph: 67,
      startTime: "08:16",
      endTime: "09:29",
      totalTime: "01:13",
      workTime: "00:56",
      idleTime: "00:17",
      off: "00:13",
      waitTime: "00:25",
      moldChangeTime: "00:14",
      errorTime: "00:16",
    },
  ],
  4: [
    {
      pressName: "Press 4",
      date: "2025-06-21",
      amount: 132,
      uph: 68,
      startTime: "08:41",
      endTime: "11:09",
      totalTime: "02:28",
      workTime: "01:50",
      idleTime: "00:38",
      off: "00:01",
      waitTime: "00:03",
      moldChangeTime: "00:13",
      errorTime: "00:20",
    },
  ],
  5: [
    {
      pressName: "Press 5",
      date: "2025-06-19",
      amount: 107,
      uph: 120,
      startTime: "08:49",
      endTime: "09:51",
      totalTime: "01:02",
      workTime: "00:58",
      idleTime: "00:04",
      off: "00:11",
      waitTime: "00:03",
      moldChangeTime: "00:22",
      errorTime: "00:05",
    },
  ],
  6: [
    {
      pressName: "Press 6",
      date: "2025-06-16",
      amount: 120,
      uph: 110,
      startTime: "08:46",
      endTime: "09:46",
      totalTime: "01:00",
      workTime: "01:00",
      idleTime: "00:00",
      off: "00:05",
      waitTime: "00:23",
      moldChangeTime: "00:00",
      errorTime: "00:09",
    },
  ],
  7: [
    {
      pressName: "Press 7",
      date: "2025-06-14",
      amount: 95,
      uph: 63,
      startTime: "08:46",
      endTime: "11:35",
      totalTime: "02:49",
      workTime: "02:08",
      idleTime: "00:41",
      off: "00:22",
      waitTime: "00:13",
      moldChangeTime: "00:11",
      errorTime: "00:18",
    },
  ],
  8: [
    {
      pressName: "Press 8",
      date: "2025-06-14",
      amount: 112,
      uph: 55,
      startTime: "08:57",
      endTime: "11:50",
      totalTime: "02:53",
      workTime: "02:45",
      idleTime: "00:08",
      off: "00:27",
      waitTime: "00:15",
      moldChangeTime: "00:14",
      errorTime: "00:13",
    },
  ],
  9: [
    {
      pressName: "Press 9",
      date: "2025-06-16",
      amount: 93,
      uph: 80,
      startTime: "08:47",
      endTime: "10:14",
      totalTime: "01:27",
      workTime: "01:20",
      idleTime: "00:07",
      off: "00:28",
      waitTime: "00:30",
      moldChangeTime: "00:14",
      errorTime: "00:05",
    },
  ],
  10: [
    {
      pressName: "Press 10",
      date: "2025-06-27",
      amount: 129,
      uph: 98,
      startTime: "08:55",
      endTime: "11:44",
      totalTime: "02:49",
      workTime: "02:33",
      idleTime: "00:16",
      off: "00:04",
      waitTime: "00:24",
      moldChangeTime: "00:21",
      errorTime: "00:03",
    },
  ],
  11: [
    {
      pressName: "Press 11",
      date: "2025-06-12",
      amount: 128,
      uph: 116,
      startTime: "08:45",
      endTime: "11:10",
      totalTime: "02:25",
      workTime: "02:03",
      idleTime: "00:22",
      off: "00:23",
      waitTime: "00:13",
      moldChangeTime: "00:27",
      errorTime: "00:08",
    },
  ],
  12: [
    {
      pressName: "Press 12",
      date: "2025-06-25",
      amount: 111,
      uph: 108,
      startTime: "08:58",
      endTime: "11:00",
      totalTime: "02:02",
      workTime: "02:00",
      idleTime: "00:02",
      off: "00:07",
      waitTime: "00:23",
      moldChangeTime: "00:22",
      errorTime: "00:30",
    },
  ],
  13: [
    {
      pressName: "Press 13",
      date: "2025-06-25",
      amount: 134,
      uph: 52,
      startTime: "08:28",
      endTime: "11:26",
      totalTime: "02:58",
      workTime: "02:30",
      idleTime: "00:28",
      off: "00:30",
      waitTime: "00:14",
      moldChangeTime: "00:18",
      errorTime: "00:06",
    },
  ],
  14: [
    {
      pressName: "Press 14",
      date: "2025-06-14",
      amount: 142,
      uph: 99,
      startTime: "08:48",
      endTime: "10:46",
      totalTime: "01:58",
      workTime: "01:33",
      idleTime: "00:25",
      off: "00:29",
      waitTime: "00:16",
      moldChangeTime: "00:29",
      errorTime: "00:19",
    },
  ],
  15: [
    {
      pressName: "Press 15",
      date: "2025-06-05",
      amount: 128,
      uph: 97,
      startTime: "08:33",
      endTime: "11:11",
      totalTime: "02:38",
      workTime: "02:26",
      idleTime: "00:12",
      off: "00:05",
      waitTime: "00:24",
      moldChangeTime: "00:15",
      errorTime: "00:15",
    },
  ],
  16: [
    {
      pressName: "Press 16",
      date: "2025-06-09",
      amount: 90,
      uph: 76,
      startTime: "08:37",
      endTime: "10:12",
      totalTime: "01:35",
      workTime: "01:30",
      idleTime: "00:05",
      off: "00:26",
      waitTime: "00:23",
      moldChangeTime: "00:05",
      errorTime: "00:07",
    },
  ],
  17: [
    {
      pressName: "Press 17",
      date: "2025-06-06",
      amount: 109,
      uph: 77,
      startTime: "08:31",
      endTime: "10:20",
      totalTime: "01:49",
      workTime: "01:28",
      idleTime: "00:21",
      off: "00:25",
      waitTime: "00:06",
      moldChangeTime: "00:25",
      errorTime: "00:12",
    },
  ],
  18: [
    {
      pressName: "Press 18",
      date: "2025-06-29",
      amount: 143,
      uph: 109,
      startTime: "08:50",
      endTime: "11:05",
      totalTime: "02:15",
      workTime: "02:03",
      idleTime: "00:12",
      off: "00:16",
      waitTime: "00:11",
      moldChangeTime: "00:21",
      errorTime: "00:09",
    },
  ],
  19: [
    {
      pressName: "Press 19",
      date: "2025-06-06",
      amount: 100,
      uph: 53,
      startTime: "08:35",
      endTime: "10:01",
      totalTime: "01:26",
      workTime: "00:54",
      idleTime: "00:32",
      off: "00:17",
      waitTime: "00:26",
      moldChangeTime: "00:02",
      errorTime: "00:17",
    },
  ],
  20: [
    {
      pressName: "Press 20",
      date: "2025-06-06",
      amount: 133,
      uph: 58,
      startTime: "08:39",
      endTime: "10:15",
      totalTime: "01:36",
      workTime: "01:33",
      idleTime: "00:03",
      off: "00:20",
      waitTime: "00:20",
      moldChangeTime: "00:22",
      errorTime: "00:04",
    },
  ],
};
